import { useMemo } from 'react';
import { graphql } from 'react-relay';
import type { GraphQLTaggedNode } from 'react-relay';

import { useEditorContentFragment } from '@confluence/editor-content-unified-query/entry-points/useEditorContentFragment.ts';
import type { EditorContentFragmentResult } from '@confluence/editor-content-unified-query/entry-points/useEditorContentFragment.ts';

import type { EditorContentStateQuery$data } from './__generated__/EditorContentStateQuery.graphql.ts';
import type { useEditorContentStateFragment_content$key } from './__generated__/useEditorContentStateFragment_content.graphql.ts';
import type { useEditorContentStateFragment_query$key } from './__generated__/useEditorContentStateFragment_query.graphql.ts';

// EditorContentStateQuery's singleContent selection. The field arguments match the refetches
// after a status change, which update these records.
const editorContentStateFragmentNode: GraphQLTaggedNode = graphql`
	fragment useEditorContentStateFragment_content on Content
	@argumentDefinitions(
		isDraft: { type: "Boolean!" }
		includeApprovals: { type: "Boolean!", defaultValue: false }
		includeLastUpdated: { type: "Boolean!", defaultValue: false }
		lastUpdatedFormat: { type: "GraphQLDateFormat", defaultValue: USER_FRIENDLY }
	)
	@catch {
		id
		contentState(isDraft: $isDraft) {
			id
			name
			color
			restrictionLevel
			isCallerPermitted
			lastModifierAaid @include(if: $includeApprovals)
		}
		contentStateLastUpdated(format: $lastUpdatedFormat, isDraft: $isDraft)
			@include(if: $includeLastUpdated) {
			value
		}
	}
`;

// Its availableContentStates selection, on Query.
const editorAvailableContentStatesFragmentNode: GraphQLTaggedNode = graphql`
	fragment useEditorContentStateFragment_query on Query
	@argumentDefinitions(contentId: { type: "ID!" })
	@catch {
		availableContentStates(contentId: $contentId) {
			customContentStates {
				name
				color
				id
			}
			spaceContentStates {
				name
				color
				id
				restrictionLevel
			}
		}
	}
`;

/**
 * The editor content state from EditorContentUnifiedQuery in the shape of
 * EditorContentStateQuery data. When it is provided, its reader skips EditorContentStateQuery.
 */
export function useEditorContentStateFragment({
	contentId,
}: {
	contentId: string;
}): EditorContentFragmentResult<EditorContentStateQuery$data> {
	const content = useEditorContentFragment<useEditorContentStateFragment_content$key>(
		'editorContentStateFragment',
		editorContentStateFragmentNode,
		{ contentId },
	);
	const availableContentStates = useEditorContentFragment<useEditorContentStateFragment_query$key>(
		'editorAvailableContentStatesFragment',
		editorAvailableContentStatesFragmentNode,
		{ contentId },
	);

	return useMemo(
		() => ({
			isProvided: content.isProvided,
			loading: content.loading,
			data: content.data && {
				singleContent: content.data,
				availableContentStates: availableContentStates.data?.availableContentStates,
			},
			error: content.error ?? availableContentStates.error,
		}),
		[content, availableContentStates],
	);
}
